import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RegisterItemList } from '../RegisterItemList';
import { PendingAiUseCasesPanel } from './PendingAiUseCasesPanel';
import { departmentsApi, peopleApi } from '../../services/api';
import {
  AI_STATUSES,
  AI_STATUS_LABELS,
  AI_STATUS_TONE,
  AI_TYPES,
  AI_TYPE_ICONS,
  AI_TYPE_LABELS,
  AI_TYPE_TONE,
} from '../../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATIONS,
  AI_ACT_CLASSIFICATION_ICONS,
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
  classificationSeverity,
} from '../../constants/aiAct';
import type { AiActClassification, AiType, AiUseCaseStatus } from '../../types/models';
import { formatDate } from '../../utils/format';

/** Tile icon per lifecycle status — the stat row above the table. */
const AI_STATUS_ICONS: Record<AiUseCaseStatus, string> = {
  draft: 'ri-draft-line',
  submitted: 'ri-send-plane-line',
  approved: 'ri-checkbox-circle-line',
  rejected: 'ri-close-circle-line',
  active: 'ri-checkbox-circle-line',
  retired: 'ri-archive-line',
};

const Dash: React.FC = () => <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>;

/** Status is a stored code (draft/submitted/…), so map it to label + tone. */
const statusChip = (status: string): React.ReactNode => {
  const key = status as AiUseCaseStatus;
  const label = AI_STATUS_LABELS[key] || status;
  const tone = AI_STATUS_TONE[key] || 'neutral';
  return <span className={`np-chip np-tone-${tone}`}>{label}</span>;
};

const aiTypeLabel = (value: unknown): string => AI_TYPE_LABELS[String(value || '') as AiType] || '';

/** Items arrive from RegisterItemList as loosely-typed rows, and every record written before
 *  the AI Act fields existed has no `aiActClassification` at all — which is `''`, the
 *  "nobody has decided yet" member, and not a missing tier. Anything unrecognised lands on
 *  `''` too rather than being passed through into a `np-tone-undefined` class. */
const classificationOf = (value: unknown): AiActClassification => {
  const raw = String(value ?? '');
  return AI_ACT_CLASSIFICATION_LABELS[raw as AiActClassification] ? (raw as AiActClassification) : '';
};

export const AiUseCaseList: React.FC = () => {
  // The AI Governance dashboard's stat tiles link here with a query param per metric, so a
  // tile click lands pre-filtered instead of on the unfiltered register - read once, on
  // mount, as the initial filter state; the user is still free to change it from here.
  const [searchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState<AiType | ''>(() => (searchParams.get('type') as AiType) || '');
  const [classificationFilter, setClassificationFilter] = useState<AiActClassification | 'any'>(
    () => (searchParams.has('classification') ? (searchParams.get('classification') as AiActClassification) || '' : 'any'),
  );
  const [statusFilter, setStatusFilter] = useState<AiUseCaseStatus | ''>(() => (searchParams.get('status') as AiUseCaseStatus) || '');
  const [personalDataOnly, setPersonalDataOnly] = useState(() => searchParams.get('personalData') === '1');
  const [specialCategoryOnly, setSpecialCategoryOnly] = useState(() => searchParams.get('specialCategory') === '1');

  // departmentId is a real foreign key (see Step2Identity.tsx); resolve it once for the whole
  // table rather than per row — `render`/`sortValue` run inside the table body, so they can't
  // call a hook themselves, only close over a value already fetched up here.
  const { data: departments = [] } = useQuery<Array<{ departmentId?: string; name?: string }>>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });
  const departmentName = (id: unknown): string =>
    departments.find((d) => d.departmentId === String(id || ''))?.name || '';

  // owner is also a real People-register foreign key now (see Step2Identity.tsx) - same
  // resolve-once-for-the-table pattern as departmentName above.
  //
  // The real People-register records carry firstName/lastName (confirmed live against the
  // DynamoDB table), not personName - trusting personName alone renders every owner blank.
  const { data: people = [] } = useQuery<Array<{ personId?: string; personName?: string; firstName?: string; lastName?: string }>>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });
  const ownerName = (id: unknown): string => {
    const match = people.find((p) => p.personId === String(id || ''));
    if (!match) return '';
    return `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.personName || '';
  };

  return (
    <>
      {/* RegisterItemList renders a full page (its own container/PageHeader/stat tiles/table) -
          no slot exists to inject content between its header and its table, so this sits in its
          own matching container directly above the whole thing rather than trying to nest
          inside it. */}
      <div className="container-xxl flex-grow-1 container-p-y np-page pb-0">
        <PendingAiUseCasesPanel />
      </div>
      <RegisterItemList
      headerActions={
        <Link className="np-btn np-btn-ghost" to="/ai-use-cases/report">
          <i className="ri-file-pdf-2-line"></i>Export PDF
        </Link>
      }
      title="AI Use Cases"
      iconClass="ri-robot-line"
      listPath="/ai-use-cases"
      detailPathPrefix="/ai-use-cases"
      idField="useCaseId"
      titleField="name"
      statusField="status"
      statusChip={statusChip}
      // One tile per status, plus the exposure count — the shape both sibling registers use
      // (ProcessingActivityList.tsx:87-99, MeetingMinutesList.tsx:150-162). Those two have
      // three statuses so they land on four tiles; this register has six (AI_STATUSES), and
      // RegisterItemList clamps its grid at five columns, so the appended tiles wrap onto a
      // second row. That is fine — there is no fifth column to spare for them.
      statCards={(items) => [
        ...AI_STATUSES.map((s) => ({
          label: AI_STATUS_LABELS[s],
          value: items.filter((item) => item.status === s).length,
          icon: AI_STATUS_ICONS[s],
          tone: AI_STATUS_TONE[s],
        })),
        {
          // Not "minimal risk" — nobody has answered the question, which is the state an AI
          // Act audit actually asks about. Loud only when there is something to be loud about.
          label: 'Not yet classified',
          value: items.filter((item) => !classificationOf(item.aiActClassification)).length,
          icon: AI_ACT_CLASSIFICATION_ICONS[''],
          tone: items.some((item) => !classificationOf(item.aiActClassification)) ? 'danger' : 'success',
        },
      ]}
      extraFilters={[
        {
          id: 'ai-type',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 220 }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as AiType | '')}
              aria-label="Filter by AI type"
            >
              <option value="">All AI Types</option>
              {AI_TYPES.map((t) => (
                <option key={t} value={t}>{AI_TYPE_LABELS[t]}</option>
              ))}
            </select>
          ),
          predicate: (item) => !typeFilter || item.aiType === typeFilter,
        },
        {
          id: 'ai-act-classification',
          control: (
            <select
              className="np-select"
              style={{ width: 'auto', maxWidth: 220 }}
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value as AiActClassification | 'any')}
              aria-label="Filter by AI Act classification"
            >
              {/* 'any' rather than '' is the sentinel for "no filter", because '' is itself a
                  selectable tier here — "not yet classified" is the one people search for. */}
              <option value="any">All Risk Tiers</option>
              {AI_ACT_CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>{AI_ACT_CLASSIFICATION_LABELS[c]}</option>
              ))}
              <option value="">{AI_ACT_CLASSIFICATION_LABELS['']}</option>
            </select>
          ),
          predicate: (item) =>
            classificationFilter === 'any'
            || classificationOf(item.aiActClassification) === classificationFilter,
        },
        // The three below have no visible control of their own - they only ever get a value
        // via the dashboard's stat-tile links (RegisterItemList's own status popover already
        // covers manual status filtering; this is a distinct "arrived pre-filtered" state), so
        // the control is just a dismissible chip that appears while the filter is active.
        {
          id: 'dashboard-status',
          control: statusFilter ? (
            <span className="np-chip np-tone-primary">
              {AI_STATUS_LABELS[statusFilter]}
              <button type="button" className="np-chip-clear" aria-label="Clear status filter" onClick={() => setStatusFilter('')}>
                <i className="ri-close-line"></i>
              </button>
            </span>
          ) : null,
          predicate: (item) => !statusFilter || item.status === statusFilter,
        },
        {
          id: 'dashboard-personal-data',
          control: personalDataOnly ? (
            <span className="np-chip np-tone-info">
              Involving personal data
              <button type="button" className="np-chip-clear" aria-label="Clear filter" onClick={() => setPersonalDataOnly(false)}>
                <i className="ri-close-line"></i>
              </button>
            </span>
          ) : null,
          predicate: (item) => !personalDataOnly || (Array.isArray(item.dataFields) && item.dataFields.some((f: any) => f?.isPersonalData !== 'no')),
        },
        {
          id: 'dashboard-special-category',
          control: specialCategoryOnly ? (
            <span className="np-chip np-tone-warning">
              Involving special-category data
              <button type="button" className="np-chip-clear" aria-label="Clear filter" onClick={() => setSpecialCategoryOnly(false)}>
                <i className="ri-close-line"></i>
              </button>
            </span>
          ) : null,
          predicate: (item) => !specialCategoryOnly || (Array.isArray(item.dataFields) && item.dataFields.some((f: any) => f?.isSpecialCategory)),
        },
        {
          // No visible control, ever - this isn't something a user toggles, it's the Pending
          // AI Use Cases gate itself. Starts at 'draft' - the checklist's own first item
          // ("AI Use Case Submitted") is what actually gates an unsubmitted draft out of the
          // main register, so a draft never appears here disconnected from why. 'active' is
          // deliberately NOT checked here at all: it is reached only via the backend's own
          // _refresh_compliance_and_maybe_activate once the checklist is already complete, so
          // an 'active' record can never fail this predicate - if one somehow did, hiding it
          // would be exactly the wrong failure mode for a compliance gate. `!== false` (not
          // `=== true`) is deliberate: a record with no complianceChecklist at all
          // ('rejected'/'retired' status, or the computation itself failed server-side) must
          // stay visible here - fail open, never silently vanish.
          id: 'compliance-checklist-incomplete',
          control: null,
          predicate: (item) =>
            (item.status !== 'draft' && item.status !== 'approved' && item.status !== 'submitted')
            || item.complianceChecklist?.complete !== false,
        },
      ]}
      extraColumns={[
        {
          header: 'AI Type',
          width: '190px',
          render: (item) => {
            const key = String(item.aiType || '') as AiType;
            if (!AI_TYPE_LABELS[key]) return <Dash />;
            return (
              <span className={`np-chip np-tone-${AI_TYPE_TONE[key]}`}>
                <i className={AI_TYPE_ICONS[key]}></i>{AI_TYPE_LABELS[key]}
              </span>
            );
          },
          sortValue: (item) => aiTypeLabel(item.aiType),
        },
        {
          header: 'Risk Tier',
          width: '160px',
          // Unlike AI Type, the unset value is rendered rather than dashed out: an empty cell
          // reads as "no risk", and "not yet classified" is the opposite of that.
          render: (item) => {
            const key = classificationOf(item.aiActClassification);
            return (
              <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[key]}`}>
                <i className={AI_ACT_CLASSIFICATION_ICONS[key]}></i>{AI_ACT_CLASSIFICATION_LABELS[key]}
              </span>
            );
          },
          // The severity index, NOT the label. Sorting the labels gives High risk → Limited →
          // Minimal → Not yet classified → Prohibited, which is alphabetical order dressed up
          // as a risk ranking and puts the one tier that blocks activation last.
          sortValue: (item) => classificationSeverity(classificationOf(item.aiActClassification)),
        },
        {
          header: 'Department',
          width: '160px',
          render: (item) => {
            const name = departmentName(item.departmentId);
            return name ? <span className="np-sub">{name}</span> : <Dash />;
          },
          sortValue: (item) => departmentName(item.departmentId),
        },
        {
          header: 'Owner',
          width: '160px',
          render: (item) => (ownerName(item.owner) ? <span className="np-sub">{ownerName(item.owner)}</span> : <Dash />),
          sortValue: (item) => ownerName(item.owner),
        },
        {
          header: 'Linked Risks',
          width: '120px',
          render: (item) => {
            const count = Array.isArray(item.linkedRiskIds) ? item.linkedRiskIds.length : 0;
            return count > 0 ? <span className="np-chip np-tone-danger">{count}</span> : <Dash />;
          },
          sortValue: (item) => (Array.isArray(item.linkedRiskIds) ? item.linkedRiskIds.length : 0),
        },
        {
          header: 'Updated',
          width: '130px',
          render: (item) => <span className="np-sub">{item.updatedAt ? formatDate(String(item.updatedAt)) : '—'}</span>,
          sortValue: (item) => String(item.updatedAt || ''),
        },
      ]}
      />
    </>
  );
};

export default AiUseCaseList;
